import React from 'react';
import PropTypes from 'prop-types';
import { useTheme, createUseStyles } from 'react-jss';
import { defineMessages, t } from '@sm/intl';
import { Container, Typography, Button } from '@sm/wds-react';
import { generateMetricsAttribute } from '@sm/metrics';
import { Logo, LogoWithText, MobileTabletScreen, DesktopScreen } from '@sm/webassets';
import { createURL } from '@sm/utils';

import './simple-hero.scss';

const COPY = defineMessages({
  SIMPLE_HERO_FORM_CALLOUT_BOLD_HEADER: {
    id: 'SimpleHero.SimpleHeroFormCalloutBoldHeader',
    defaultMessage: 'Get answers with surveys',
    description:
      "[Type: Header][Vis: high] - The Hero's Primary tagline. A brief description of SurveyMonkey's functionality.",
  },
  SIMPLE_HERO_FORM_CALLOUT_CTA: {
    id: 'SimpleHero.SimpleHeroFormCalloutCTA',
    defaultMessage: 'CREATE SURVEY',
    description:
      "[Type: Header][Vis: high] - The Hero's only CTA. It takes user's to a login screen or directly to a dashboard to create their survey.",
  },
});

const useStyles = createUseStyles(theme => {
  return {
    smWebRespWebButton: {
      backgroundColor: theme.brandColors.accent.bumblebee,
      color: theme.brandColors.neutral.charcoal,
      border: 'none',
      '&:hover, &:focus': {
        backgroundColor: theme.endpageColors.onHover.lighterBumblebee,
      },
    },
    smGoldie: {
      '&:hover, &:focus': {
        textDecoration: 'none',
      },
    },
  };
});

const SimpleHero = ({ hideCTAs, utSource, utSource2 }) => {
  const theme = useTheme();
  const classes = useStyles({ theme });

  return (
    <Container
      data-testid="SimpleHero"
      className="sm-simple-hero"
      flex
      flexDirection="column"
      flexJustify="center"
      px={3}
      pt={7}
      pb={8}
    >
      <DesktopScreen>
        <a alt="SurveyMonkey Logo" data-testid="SimpleHero__Logo" href={createURL('/', {})}>
          <LogoWithText color="alt" className="sm-simple-hero__logo" />
        </a>
        <Container mx={1} mt={7} mb={5}>
          <Typography component="h3" color="light" variant="hero1" align="center">
            <strong>{t(COPY.SIMPLE_HERO_FORM_CALLOUT_BOLD_HEADER)}</strong>
          </Typography>
        </Container>
      </DesktopScreen>
      <MobileTabletScreen>
        <a
          alt="SurveyMonkey Goldie"
          data-testid="SimpleHero__Goldie"
          href={createURL('/', {})}
          className={classes.smGoldie}
        >
          <Logo color="alt" className="sm-simple-logo-header__text" />
        </a>
        <Container mx={1} mt={7} mb={5}>
          <Typography component="h3" color="light" variant="pageTitle" align="center">
            <strong>{t(COPY.SIMPLE_HERO_FORM_CALLOUT_BOLD_HEADER)}</strong>
          </Typography>
        </Container>
      </MobileTabletScreen>
      <If condition={!hideCTAs}>
        <Button
          size="lg"
          className={classes.smWebRespWebButton}
          href={createURL('/create/', {
            ut_source: utSource,
            ut_source2: utSource2,
          })}
          data-sm-metric={generateMetricsAttribute({
            data: {
              actionType: `${utSource}_click_${utSource2}_cta_to_create`,
              actionFlow: { utSource },
            },
          })}
        >
          {t(COPY.SIMPLE_HERO_FORM_CALLOUT_CTA)}
        </Button>
      </If>
    </Container>
  );
};

SimpleHero.propTypes = {
  hideCTAs: PropTypes.bool,
  utSource: PropTypes.string,
  utSource2: PropTypes.string,
};

SimpleHero.defaultProps = {
  utSource: 'survey_thanks',
  utSource2: 'rebrand_existing_user',
  hideCTAs: true,
};

export default SimpleHero;
